import React, { useState, useCallback, useRef, useEffect } from "react";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import ModernPlaceAutocomplete from "../../../../core/common/ModernPlaceAutocomplete";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import CollapseHeader from "../../../../core/common/collapse-header";
import { checkPermission } from "../../../../core/permissons/CheckPermissons";
import apiDefinitions from "../../../../core/api/apiDefinitions";
import { showSuccessAlert, showError, showSuccessUpdateAlert, showDeleteConfirmation } from "../../../../core/alert/sweetAlertConfig";
import type { Library } from "@googlemaps/js-api-loader";
import GlassSelect from "../../../../core/common/glassSelect/GlassSelect";


const GOOGLE_MAPS_LIBRARIES: Library[] = ["places"];

const navItems = [
  { label: 'View Rooms', icon: 'ti ti-settings', link: all_routes.viewrooms, permission: 'ROLE_ADD_LOCATION' },
  { label: 'Room Register', icon: 'ti ti-settings', link: all_routes.roomregister, permission: 'ROLE_ADD_LOCATION' },
  { label: 'Add Location', icon: 'ti ti-plus', link: all_routes.location, permission: 'ROLE_ADD_LOCATION' },
    { label: 'View Parking Slots', icon: 'ti ti-settings', link: all_routes.viewparkingslots, permission: 'ROLE_GET_PARKING_SLOT' },
  { label: 'Parking Slot Register', icon: 'ti ti-settings', link: all_routes.parkingslotregister, permission: 'ROLE_GET_PARKING_SLOT' },
]


const googleMapsApiKey =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  "AIzaSyC7S0DKmKpZmtidyMto2PiIXhR5miu9Pi0";

interface LocationPayload {
  name: string;
  address: string;
  lat: string;
  lng: string;
  description: string;
}

interface UpdateLocationPayload extends LocationPayload {
  id: number;
}

interface Location {
  id: number;
  name: string;
  address: string;
  lat: number;
  lng: number;
  description: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
}

interface GetLocationsResponse {
  locations: Location[];
  total: number;
}

const containerStyle = {
  height: "300px",
  width: "100%",
};

const center = {
  lat: 6.9271, // Default center (Colombo, Sri Lanka)
  lng: 79.8612,
};

const Location: React.FC = () => {
  const navigate = useNavigate();
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [name, setName] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [lat, setLat] = useState<string>("");
  const [lng, setLng] = useState<string>("");
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const checkToken = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      showError("Authentication token not found. Please log in.");
      navigate(all_routes.login);
      return false;
    }
    return token;
  };

  const fetchLocations = async () => {
    if (!checkToken()) return;
    try {
      const payload = { filterType: "", filterValue: "", date: "" };
      const response = await apiDefinitions.getLocations("1", payload);
      if (response.status === 200) {
        const data: GetLocationsResponse = response.data.data;
        setLocations(data.locations || []);
      } else {
        showError(response.data?.statusDescription || "Failed to fetch locations");
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError("Unauthorized: Invalid or expired token. Please log in again.");
        localStorage.removeItem("token");
        navigate(all_routes.login);
      } else {
        showError("Failed to fetch locations. Please try again.");
      }
    }
  };

  const fetchLocationById = async (id: number) => {
    if (!checkToken()) return;
    try {
      const response = await apiDefinitions.getLocationById(id);
      if (response.status === 200) {
        const location: Location = response.data.data;
        setName(location.name);
        setAddress(location.address);
        setDescription(location.description);
        setLat(location.lat.toString());
        setLng(location.lng.toString());
        setMarkerPosition({ lat: location.lat, lng: location.lng });
        setSelectedLocationId(location.id);
        setIsEditMode(true);
        if (mapRef.current) {
          mapRef.current.panTo({ lat: location.lat, lng: location.lng });
          mapRef.current.setZoom(15);
        }
      } else {
        showError(response.data?.statusDescription || "Failed to fetch location");
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError("Unauthorized: Invalid or expired token. Please log in again.");
        localStorage.removeItem("token");
        navigate(all_routes.login);
      } else {
        showError("Failed to fetch location. Please try again.");
      }
    }
  };

  const addLocation = async (payload: LocationPayload) => {
    if (!checkToken()) return;
    setIsSubmitting(true);
    try {
      const response = await apiDefinitions.addLocation(payload);
      if (response.status === 200 || response.status === 201) {
        showSuccessAlert("Location added successfully");
        resetForm();
        await fetchLocations();
      } else {
        showError(response.data?.statusDescription || "Failed to add location");
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError("Unauthorized: Invalid or expired token. Please log in again.");
        localStorage.removeItem("token");
        navigate(all_routes.login);
      } else {
        showError("Failed to add location. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateLocation = async (payload: UpdateLocationPayload) => {
    if (!checkToken()) return;
    setIsSubmitting(true);
    try {
      const response = await apiDefinitions.updateLocation(payload);
      if (response.status === 200) {
        showSuccessUpdateAlert("Location updated successfully");
        resetForm();
        await fetchLocations();
      } else {
        showError(response.data?.statusDescription || "Failed to update location");
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError("Unauthorized: Invalid or expired token. Please log in again.");
        localStorage.removeItem("token");
        navigate(all_routes.login);
      } else if (error.response?.status === 404) {
        showError("Location not found");
      } else if (error.response?.status === 409) {
        showError("Location name already exists");
      } else {
        showError("Failed to update location. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteLocation = async (id: number) => {
    if (!checkToken()) return;
    setIsSubmitting(true);
    try {
      const response = await apiDefinitions.deleteLocation(id);
      if (response.status === 200) {
        resetForm();
        await fetchLocations();
      } else {
        showError(response.data?.statusDescription || "Failed to delete location");
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError("Unauthorized: Invalid or expired token. Please log in again.");
        localStorage.removeItem("token");
        navigate(all_routes.login);
      } else if (error.response?.status === 404) {
        showError("Location not found");
      } else {
        showError("Failed to delete location. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setName("");
    setAddress("");
    setDescription("");
    setLat("");
    setLng("");
    setMarkerPosition(null);
    setSelectedLocationId(null);
    setIsEditMode(false);
    if (mapRef.current) {
      mapRef.current.panTo(center);
      mapRef.current.setZoom(12);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name || !address || !lat || !lng || !description) {
      showError("Please provide all required fields: name, address, and description");
      return;
    }
    const payload: LocationPayload = { name, address, lat, lng, description };
    if (isEditMode && selectedLocationId !== null) {
      updateLocation({ ...payload, id: selectedLocationId });
    } else {
      addLocation(payload);
    }
  };

  const handleDelete = () => {
    if (selectedLocationId === null) {
      showError("No location selected for deletion");
      return;
    }
    showDeleteConfirmation(() => deleteLocation(selectedLocationId));
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry && place.geometry.location && place.formatted_address) {
        const lat = place.geometry.location.lat().toString();
        const lng = place.geometry.location.lng().toString();
        setLat(lat);
        setLng(lng);
        setAddress(place.formatted_address);
        setMarkerPosition({ lat: parseFloat(lat), lng: parseFloat(lng) });
        if (mapRef.current) {
          mapRef.current.panTo({ lat: parseFloat(lat), lng: parseFloat(lng) });
          mapRef.current.setZoom(15);
        }
      } else {
        showError("Invalid location selected. Please choose a valid address.");
      }
    }
  };

  const onMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = event.latLng.lat().toString();
      const lng = event.latLng.lng().toString();
      setLat(lat);
      setLng(lng);
      setAddress(""); // Clear address as it's user-clicked
      setMarkerPosition({ lat: parseFloat(lat), lng: parseFloat(lng) });
    }
  }, []);

  const onLoadMap = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const handleViewLocation = (location: Location | undefined) => {
    if (!location || !location.lat || !location.lng) {
      setMarkerPosition(null);
      if (mapRef.current) {
        mapRef.current.panTo(center);
        mapRef.current.setZoom(12);
      }
      return;
    }
    const position = { lat: location.lat, lng: location.lng };
    setMarkerPosition(position);
    if (mapRef.current) {
      mapRef.current.panTo(position);
      mapRef.current.setZoom(15);
    }
    fetchLocationById(location.id);
  };

  // Fetch locations on component mount
  useEffect(() => {
    fetchLocations();
  }, []);


  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Administration/Location/Add Location</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body pb-0 pt-2">
                <ul className="nav nav-tabs nav-tabs-bottom" style={{ gap: "12px", display: "flex" }}>
                  {navItems.map((item, index) =>
                    item.permission && checkPermission(item.permission) ? (
                      <li className="nav-item me-item" key={index}>
                        <Link
                          to={item.link}
                          className={`nav-link px-0 ${index === 2 ? "active" : ""}`}
                        >
                          <i className={item.icon}></i> {item.label}
                        </Link>
                      </li>
                    ) : null
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-header justify-content-between">
            <div className="card-title">{isEditMode ? "Edit Location" : "Add New Location"}</div>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Location Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter location name"
                      value={name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Address</label>
                    <ModernPlaceAutocomplete
                      value={address}
                      placeholder="Search for an address"
                      onChange={(val) => setAddress(val)}
                      onPlaceSelect={(selectedAddress, selectedLat, selectedLng) => {
                        setAddress(selectedAddress);
                        setLat(selectedLat);
                        setLng(selectedLng);
                        const latNum = parseFloat(selectedLat);
                        const lngNum = parseFloat(selectedLng);
                        if (!isNaN(latNum) && !isNaN(lngNum)) {
                          setMarkerPosition({ lat: latNum, lng: lngNum });
                          if (mapRef.current) {
                            mapRef.current.panTo({ lat: latNum, lng: lngNum });
                            mapRef.current.setZoom(15);
                          }
                        }
                      }}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      placeholder="Enter description"
                      value={description}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <button
                      type="submit"
                      className="btn btn-success me-2"
                      disabled={isSubmitting || (isEditMode && !checkPermission("ROLE_ADD_LOCATION"))}
                    >
                      {isSubmitting ? "Submitting..." : isEditMode ? "Update Location" : "Add Location"}
                    </button>
                    {isEditMode && checkPermission("ROLE_ADD_LOCATION") && (
                      <>
                        <button
                          type="button"
                          className="btn btn-danger me-2"
                          disabled={isSubmitting}
                          onClick={handleDelete}
                        >
                          Delete Location
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          disabled={isSubmitting}
                          onClick={resetForm}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </form>
                <div className="mb-3 mt-4">
                  <label className="form-label">Saved Locations</label>
                  <GlassSelect
                    options={locations.map((location) => ({
                      value: location.name,
                      label: `${location.name} (${location.address})`,
                    }))}
                    onChange={(val) => handleViewLocation(locations.find(loc => loc.name === val))}
                    placeholder={locations.length === 0 ? "No locations available" : "Select a location"}
                    searchable
                    allowClear
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  {loadError ? (
                    <div className="alert alert-danger p-3">
                      Failed to load Google Maps: {loadError.message}
                    </div>
                  ) : isLoaded ? (
                    <GoogleMap
                      mapContainerStyle={containerStyle}
                      center={center}
                      zoom={12}
                      onClick={onMapClick}
                      onLoad={onLoadMap}
                    >
                      {markerPosition && <Marker position={markerPosition} />}
                    </GoogleMap>
                  ) : (
                    <div
                      style={{
                        ...containerStyle,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(241, 245, 249, 0.7)",
                        borderRadius: "12px",
                        border: "1px dashed #CBD5E1",
                      }}
                    >
                      <div className="text-center text-muted">
                        <div className="spinner-border spinner-border-sm text-primary mb-2" role="status" />
                        <div style={{ fontSize: "0.85rem" }}>Loading Map Preview...</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Location;